import React from "react";
import { Eye } from "lucide-react";

/**
 * FocusCard — a quiet summary of exceptions and patterns for the Guide.
 *
 * Focus does not repeat individual NOW items. It only speaks when
 * something has slipped across multiple items, or when a time-anchored
 * responsibility is overdue. When quiet, the card renders a small
 * intentional empty state rather than filling space with noise.
 */

const PRIORITY_TONE = {
  urgent:    "border-amber-200 bg-amber-50/60",
  important: "border-stone-200 bg-stone-50/60",
  standard:  "border-stone-200 bg-white",
};

export default function FocusCard({ focus }) {
  const items = focus?.items || [];
  const quiet = !!focus?.quiet;
  return (
    <section
      className="bg-white subtle-border rounded-2xl p-6"
      data-testid="focus-card"
      aria-labelledby="focus-card-title"
    >
      <header className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] label-eyebrow text-stone-400">Guide Journey</div>
          <h2
            id="focus-card-title"
            className="font-serif-display text-2xl text-stone-900 mt-1"
            style={{ fontWeight: 400 }}
          >
            Focus
          </h2>
        </div>
        <Eye size={14} strokeWidth={1.25} className="text-stone-400" />
      </header>

      {quiet || items.length === 0 ? (
        <div
          className="text-sm text-stone-500 leading-relaxed"
          data-testid="focus-empty"
        >
          Nothing to hold your attention at the pattern level.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="focus-list">
          {items.map((it) => (
            <li
              key={it.id}
              className={`rounded-xl border p-3 subtle-border ${PRIORITY_TONE[it.priority] || PRIORITY_TONE.standard}`}
              data-testid={`focus-item-${it.kind}`}
            >
              <div className="text-sm text-stone-900" style={{ fontWeight: 500 }}>
                {it.title}
              </div>
              {it.description && (
                <p className="text-xs text-stone-500 mt-1 leading-relaxed">{it.description}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
